import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, Wallet, ArrowUpCircle, ArrowDownCircle, History, Settings, 
  Bell, Copy, Search, Download, Calendar, Check, ChevronDown, QrCode, Shield, HelpCircle, LogOut
} from "lucide-react";

export default function CoinPayHistory() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [showBrowser, setShowBrowser] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Parse date from DD.MM.YYYY to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [day, month, year] = dateStr.split('.');
    return new Date(year, month - 1, day);
  };

  // Format date for display (DD.MM.YYYY)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU');
  };

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    const browserParam = searchParams.get('browser');
    
    if (themeParam) setBrowserTheme(themeParam);
    if (browserParam === 'false') setShowBrowser(false);

    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'deposit', 'withdrawal'].includes(filterParam)) {
      setTypeFilter(filterParam);
    }
    
    if (dataParam) {
      try {
        const parsedData = (() => { try { return JSON.parse(dataParam); } catch(_) { return JSON.parse(decodeURIComponent(dataParam)); } })();
        setData(parsedData);
        
        // Check showBrowser from data
        if (parsedData.showBrowser === false) setShowBrowser(false);
        if (parsedData.browserTheme) setBrowserTheme(parsedData.browserTheme);
        
        // Calculate date range from transactions - keep original DD.MM.YYYY format
        if (parsedData.transactions && parsedData.transactions.length > 0) {
          const validDates = parsedData.transactions
            .map(tx => {
              if (!tx.date) return null;
              const parts = tx.date.split('.');
              if (parts.length !== 3) return null;
              const [day, month, year] = parts;
              // Return original date string and parsed for sorting
              return { 
                original: tx.date, 
                sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
              };
            })
            .filter(d => d !== null);
          
          if (validDates.length > 0) {
            validDates.sort((a, b) => a.sortValue - b.sortValue);
            // Store original format dates directly
            setDateFrom(validDates[0].original);
            setDateTo(validDates[validDates.length - 1].original);
          }
        }
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
    document.title = 'История | CoinPay';
    
    // Set body background to match content (fixes zoom issue)
    document.body.style.backgroundColor = '#f8fafc';
    document.documentElement.style.backgroundColor = '#f8fafc';
    
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/coinpay-favicon.svg';
    
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [searchParams]);

  if (!data) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">Загрузка...</div>;
  }

  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '000000';
  const transactions = data.transactions || [];

  // Helper to convert DD.MM.YYYY to sortable number
  const dateToSortValue = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split('.');
    if (parts.length !== 3) return 0;
    const [day, month, year] = parts;
    return parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day);
  };

  // Sort transactions by date (newest first) and apply filters
  const filteredTransactions = transactions
    .map(tx => ({ ...tx, sortValue: dateToSortValue(tx.date) }))
    .sort((a, b) => b.sortValue - a.sortValue)
    .filter(tx => {
      // Type filter
      if (typeFilter !== 'all' && tx.type !== typeFilter) return false;
      // Date filter using sortable values
      if (dateFrom) {
        const fromValue = dateToSortValue(dateFrom);
        if (tx.sortValue < fromValue) return false;
      }
      if (dateTo) {
        const toValue = dateToSortValue(dateTo);
        if (tx.sortValue > toValue) return false;
      }
      return true;
    });

  const menuItems = [
    { icon: Home, label: 'Главная', active: false },
    { icon: Wallet, label: 'Кошелёк', active: false },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowDownCircle, label: 'Вывести', active: false },
    { icon: History, label: 'История', active: true },
    { icon: QrCode, label: 'QR-код', active: false },
    { icon: Shield, label: 'Безопасность', active: false },
    { icon: Settings, label: 'Настройки', active: false },
  ];

  const bottomItems = [
    { icon: HelpCircle, label: 'Поддержка' },
    { icon: LogOut, label: 'Выйти' },
  ];

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/coinpay-favicon.svg" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>История | CoinPay</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>coinpay.ru/history</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: showBrowser ? theme.bg : '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {showBrowser && <BrowserFrame />}
      
      <div className="flex-1 flex" style={{ backgroundColor: '#f8fafc' }}>
        {/* Sidebar */}
        <aside className="w-56 bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex flex-col flex-shrink-0">
          <div className="p-4 flex items-center gap-2">
            <img src="/coinpay-logo-white.svg" alt="CoinPay" className="h-9 w-auto" style={{ maxWidth: '180px' }} />
          </div>

          <nav className="flex-1 py-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a key={index} href="#" className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${item.active ? 'text-[#FFD700] bg-white/10' : 'text-gray-300 hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </a>
              );
            })}
            
            <div className="mt-4 border-t border-white/10 pt-2">
              {bottomItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a key={index} href="#" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400 hover:bg-white/5">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </a>
                );
              })}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">История операций</h1>
            <div className="flex items-center gap-4">
              <button className="p-2 text-gray-500 hover:text-gray-700 relative">
                <Bell className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}</span>
                </div>
                <span className="text-sm font-medium">{accountName}</span>
                <span className="text-xs text-gray-500">ID: {userId}</span>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-6 overflow-auto">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input type="text" placeholder="Поиск по транзакциям" className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white" />
              </div>
              
              <div className="relative">
                <button onClick={() => setShowTypeDropdown(!showTypeDropdown)} className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white min-w-[140px]">
                  <span>{typeFilter === 'all' ? 'Все типы' : typeFilter === 'deposit' ? 'Выводы' : 'Пополнения'}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <button onClick={() => { setTypeFilter('all'); setShowTypeDropdown(false); }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'all' ? 'bg-blue-50 text-blue-600' : ''}`}>Все типы</button>
                    <button onClick={() => { setTypeFilter('deposit'); setShowTypeDropdown(false); }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'deposit' ? 'bg-blue-50 text-blue-600' : ''}`}>Выводы</button>
                    <button onClick={() => { setTypeFilter('withdrawal'); setShowTypeDropdown(false); }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'withdrawal' ? 'bg-blue-50 text-blue-600' : ''}`}>Пополнения</button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">с</span>
                <span className="text-gray-700 font-medium">
                  {dateFrom || '—'}
                </span>
                <span className="text-gray-500">по</span>
                <span className="text-gray-700 font-medium">
                  {dateTo || '—'}
                </span>
              </div>

              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                <Download className="w-4 h-4" />
                <span>Экспорт</span>
              </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Статус</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Дата/Время</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Тип</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Валюта</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-400">Нет транзакций</td>
                    </tr>
                  ) : (
                    filteredTransactions.map((tx, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-green-500" />
                            </div>
                            <span className="text-sm text-green-500">Выполнено</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{tx.date}</div>
                          <div className="text-xs text-gray-400">{tx.time}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900">{tx.type === 'deposit' ? 'Вывод' : 'Пополнение'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900">{tx.currency}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${tx.type === 'deposit' ? 'text-red-500' : 'text-green-500'}`}>
                            {tx.type === 'deposit' ? '-' : '+'}{tx.amount} {tx.currency}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
